/**
 * Component: Product gallery
 * Danish Modern L.A. Shopify Theme
 *
 * The viewport is a native scroll-snap strip, so swiping works without this
 * script. The component adds:
 * - thumbnail buttons that jump to a slide
 * - keeping the current thumbnail in sync while the visitor swipes
 * - jumping to a variant's image when product-info announces a variant change
 */

if (!customElements.get('product-gallery')) {
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-gallery-viewport]');
      if (!this.viewport) return;

      this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));

      this.thumbsTrack = this.querySelector('[data-gallery-thumbs]');
      this.thumbPrevBtn = this.querySelector('[data-gallery-thumb-prev]');
      this.thumbNextBtn = this.querySelector('[data-gallery-thumb-next]');

      // Prev/next arrows overlaid on the image (artful layout).
      this.prevBtn = this.querySelector('[data-gallery-prev]');
      this.nextBtn = this.querySelector('[data-gallery-next]');
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.step(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.step(1));

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.galleryThumb)));
      });

      if (this.thumbsTrack) {
        this.initThumbsScroll();
      }

      // The slide that is mostly visible becomes the current one.
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.setCurrent(this.slides.indexOf(entry.target));
          });
        },
        { root: this.viewport, threshold: 0.6 }
      );
      this.slides.forEach((slide) => this.observer.observe(slide));

      this.onVariantChange = (event) => {
        if (event.detail.galleryId !== this.id || !event.detail.mediaId) return;
        const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(event.detail.mediaId));
        if (index > -1) this.goTo(index);
      };
      document.addEventListener('product:variant-change', this.onVariantChange);

      // Open on the variant's image rather than always the first one.
      const start = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
      if (start > 0) this.goTo(start, 'instant');
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      document.removeEventListener('product:variant-change', this.onVariantChange);
      if (this.thumbsResizeObserver) {
        this.thumbsResizeObserver.disconnect();
      }
      if (this.onMouseMove) {
        window.removeEventListener('mousemove', this.onMouseMove);
      }
      if (this.onMouseUp) {
        window.removeEventListener('mouseup', this.onMouseUp);
      }
    }

    initThumbsScroll() {
      const track = this.thumbsTrack;
      if (!track) return;

      const updateArrows = () => {
        const hasOverflow = track.scrollWidth > track.clientWidth + 4;
        if (!hasOverflow) {
          if (this.thumbPrevBtn) this.thumbPrevBtn.hidden = true;
          if (this.thumbNextBtn) this.thumbNextBtn.hidden = true;
          return;
        }

        if (this.thumbPrevBtn) {
          this.thumbPrevBtn.hidden = false;
          this.thumbPrevBtn.disabled = track.scrollLeft <= 4;
        }
        if (this.thumbNextBtn) {
          this.thumbNextBtn.hidden = false;
          this.thumbNextBtn.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
        }
      };

      this.updateThumbsArrows = updateArrows;
      track.addEventListener('scroll', updateArrows, { passive: true });

      if (this.thumbPrevBtn) {
        this.thumbPrevBtn.addEventListener('click', () => {
          const scrollStep = Math.max(120, track.clientWidth * 0.75);
          track.scrollBy({ left: -scrollStep, behavior: 'smooth' });
        });
      }

      if (this.thumbNextBtn) {
        this.thumbNextBtn.addEventListener('click', () => {
          const scrollStep = Math.max(120, track.clientWidth * 0.75);
          track.scrollBy({ left: scrollStep, behavior: 'smooth' });
        });
      }

      // Mouse wheel horizontal scroll over thumbnail strip
      this.onThumbWheel = (e) => {
        if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
          if (track.scrollWidth > track.clientWidth + 4) {
            const atStart = track.scrollLeft <= 0 && e.deltaY < 0;
            const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1 && e.deltaY > 0;
            if (!atStart && !atEnd) {
              e.preventDefault();
              track.scrollLeft += e.deltaY;
            }
          }
        }
      };
      track.addEventListener('wheel', this.onThumbWheel, { passive: false });

      // Drag to scroll for mouse users
      let isDown = false;
      let startX = 0;
      let scrollStart = 0;
      let isDragging = false;

      track.addEventListener('mousedown', (e) => {
        if (track.scrollWidth <= track.clientWidth) return;
        isDown = true;
        isDragging = false;
        startX = e.pageX - track.offsetLeft;
        scrollStart = track.scrollLeft;
      });

      this.onMouseMove = (e) => {
        if (!isDown) return;
        const x = e.pageX - track.offsetLeft;
        const walk = x - startX;
        if (Math.abs(walk) > 5) {
          if (!isDragging) {
            isDragging = true;
            track.style.cursor = 'grabbing';
            track.style.userSelect = 'none';
          }
          track.scrollLeft = scrollStart - walk;
        }
      };
      window.addEventListener('mousemove', this.onMouseMove);

      this.onMouseUp = () => {
        if (!isDown) return;
        isDown = false;
        track.style.cursor = '';
        track.style.userSelect = '';
        if (isDragging) {
          const preventClick = (e) => {
            e.stopImmediatePropagation();
            e.preventDefault();
            window.removeEventListener('click', preventClick, true);
          };
          window.addEventListener('click', preventClick, true);
          setTimeout(() => {
            window.removeEventListener('click', preventClick, true);
            isDragging = false;
          }, 50);
        }
      };
      window.addEventListener('mouseup', this.onMouseUp);

      // Handle resize
      if (typeof ResizeObserver !== 'undefined') {
        this.thumbsResizeObserver = new ResizeObserver(() => updateArrows());
        this.thumbsResizeObserver.observe(track);
      }

      // Initial check
      updateArrows();
      requestAnimationFrame(updateArrows);
    }

    step(delta) {
      const current = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      let next = current + delta;
      if (next < 0) next = this.slides.length - 1;
      if (next >= this.slides.length) next = 0;
      this.goTo(next);
    }

    goTo(index, behavior = 'smooth') {
      const slide = this.slides[index];
      if (!slide) return;
      this.viewport.scrollTo({ left: slide.offsetLeft - this.viewport.offsetLeft, behavior });
      this.setCurrent(index);
    }

    setCurrent(index) {
      this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
      this.thumbs.forEach((thumb, i) => {
        if (i === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });

      const activeThumb = this.thumbs[index];
      if (activeThumb && this.thumbsTrack) {
        const item = activeThumb.closest('.product-gallery__thumb-item') || activeThumb;
        const itemLeft = item.offsetLeft;
        const itemRight = itemLeft + item.offsetWidth;
        const scrollLeft = this.thumbsTrack.scrollLeft;
        const clientWidth = this.thumbsTrack.clientWidth;

        if (itemLeft < scrollLeft) {
          this.thumbsTrack.scrollTo({ left: Math.max(0, itemLeft - 16), behavior: 'smooth' });
        } else if (itemRight > scrollLeft + clientWidth) {
          this.thumbsTrack.scrollTo({ left: itemRight - clientWidth + 16, behavior: 'smooth' });
        }
      }
    }
  }

  customElements.define('product-gallery', ProductGallery);
}
